import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header';
import CreateEditRoleModal from './CreateEditRoleModal';
import { roleService } from '../../services/roleService';
import { Role, Permission, PermissionCategory } from '../../types/role';
import { useToast } from '../../contexts/ToastContext';

/* -- Role Type Badge ------------------------------------------- */
const RoleTypeBadge: React.FC<{ isSystemRole: boolean }> = ({ isSystemRole }) => {
  const config = isSystemRole
    ? { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20', label: 'System' }
    : { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', label: 'Customer' };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${config.bg} ${config.text} border ${config.border}`}>
      {isSystemRole ? '★ ' : ''}{config.label}
    </span>
  );
};

/* -- Delete Confirm Dialog ------------------------------------- */
const DeleteConfirm: React.FC<{
  role: Role;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}> = ({ role, onConfirm, onCancel, isDeleting }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-white border border-slate-200 rounded-2xl p-8 w-full max-w-md text-center">
      <div className="size-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="material-symbols-outlined text-3xl text-red-500">warning</span>
      </div>
      <h3 className="text-xl font-bold mb-2 text-slate-900">Delete Role</h3>
      <p className="text-slate-500 text-sm mb-6">
        Are you sure you want to delete <strong className="text-slate-900">{role.name}</strong>?
        This action cannot be undone.
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" disabled={isDeleting}>Cancel</button>
        <button onClick={onConfirm} className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center justify-center gap-2" disabled={isDeleting}>
          {isDeleting ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Deleting...</>) : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

/* -- Permission Viewer (inline expand) ------------------------- */
const PermissionViewer: React.FC<{
  role: Role;
  allCategories: PermissionCategory[];
  onSavePermissions: (roleId: string, permissionIds: string[]) => void;
  isSaving: boolean;
}> = ({ role, allCategories, onSavePermissions, isSaving }) => {
  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(role.permissions.map(p => p.id))
  );

  useEffect(() => {
    setSelectedIds(new Set(role.permissions.map(p => p.id)));
  }, [role.permissions]);

  const togglePermission = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (permissions: Permission[]) => {
    const allSelected = permissions.every(p => selectedIds.has(p.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      permissions.forEach(p => {
        if (allSelected) next.delete(p.id);
        else next.add(p.id);
      });
      return next;
    });
  };

  const handleSave = () => {
    onSavePermissions(role.id, Array.from(selectedIds));
    setEditMode(false);
  };

  const handleCancel = () => {
    setSelectedIds(new Set(role.permissions.map(p => p.id)));
    setEditMode(false);
  };

  return (
    <div className="px-6 pb-6">
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xs font-black uppercase tracking-widest text-slate-500">
            Permissions ({role.permissions.length})
          </h4>
          {!editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="text-xs font-bold text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-sm">edit</span>
              Edit Permissions
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="text-xs font-bold bg-primary hover:bg-primary/90 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                disabled={isSaving}
              >
                {isSaving ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <span className="material-symbols-outlined text-sm">save</span>
                )}
                Save
              </button>
            </div>
          )}
        </div>

        {allCategories.length === 0 ? (
          <p className="text-slate-500 text-sm">No permissions available</p>
        ) : (
          <div className="space-y-4">
            {allCategories.map(cat => {
              const catSelectedCount = cat.permissions.filter(p => selectedIds.has(p.id)).length;
              const allCatSelected = catSelectedCount === cat.permissions.length;

              return (
                <div key={cat.category}>
                  <div className="flex items-center gap-2 mb-2">
                    {editMode && (
                      <button
                        onClick={() => toggleCategory(cat.permissions)}
                        className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                          allCatSelected
                            ? 'bg-primary border-primary text-white'
                            : catSelectedCount > 0
                            ? 'bg-primary/30 border-primary/50 text-white'
                            : 'border-slate-300 hover:border-slate-400'
                        }`}
                      >
                        {(allCatSelected || catSelectedCount > 0) && (
                          <span className="material-symbols-outlined text-xs">
                            {allCatSelected ? 'check' : 'remove'}
                          </span>
                        )}
                      </button>
                    )}
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      {cat.category}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      ({catSelectedCount}/{cat.permissions.length})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 ml-6">
                    {cat.permissions.map(p => {
                      const isSelected = selectedIds.has(p.id);
                      if (!editMode && !isSelected) return null;

                      return (
                        <button
                          key={p.id}
                          onClick={() => editMode && togglePermission(p.id)}
                          disabled={!editMode}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                            isSelected
                              ? 'bg-primary/10 text-primary border border-primary/20'
                              : 'bg-slate-100 text-slate-500 border border-slate-200 hover:border-slate-300'
                          } ${editMode ? 'cursor-pointer' : 'cursor-default'}`}
                          title={p.description || p.name}
                        >
                          {editMode && (
                            <span className="material-symbols-outlined text-xs">
                              {isSelected ? 'check_box' : 'check_box_outline_blank'}
                            </span>
                          )}
                          {p.code}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* -- Roles & Permissions Page ---------------------------------- */
const RolesPermissionsPage: React.FC = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'system' | 'customer'>('all');

  const [roles, setRoles] = useState<Role[]>([]);
  const [allCategories, setAllCategories] = useState<PermissionCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [rolesRes, catsRes] = await Promise.all([
        roleService.getAll(),
        roleService.getPermissionsByCategory(),
      ]);

      if (rolesRes.success) setRoles(rolesRes.data);
      else setError(rolesRes.message || 'Failed to load roles');

      if (catsRes.success) setAllCategories(catsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredRoles = roles.filter((role) => {
    if (activeFilter === 'system' && !role.isSystemRole) return false;
    if (activeFilter === 'customer' && role.isSystemRole) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        role.name.toLowerCase().includes(q) ||
        role.description?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleSavePermissions = async (roleId: string, permissionIds: string[]) => {
    setIsSavingPermissions(true);
    try {
      const res = await roleService.updatePermissions(roleId, { permissionIds });
      if (res.success) {
        toast.success('Permissions updated');
        fetchData();
      } else {
        toast.error(res.message || 'Failed to update permissions');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update permissions');
    } finally {
      setIsSavingPermissions(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await roleService.delete(deleteTarget.id);
      if (res.success) {
        toast.success(`${deleteTarget.name} deleted`);
        setDeleteTarget(null);
        fetchData();
      } else {
        toast.error(res.message || 'Failed to delete role');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete role');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (role: Role) => {
    setEditRole(role);
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    setEditRole(null);
    setIsModalOpen(true);
  };

  const systemCount = roles.filter(r => r.isSystemRole).length;
  const customerCount = roles.filter(r => !r.isSystemRole).length;

  return (
    <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
      <Header searchPlaceholder="Search roles by name or description..." onSearch={setSearchQuery} />

      <div className="flex-1 overflow-y-auto p-8">
        {/* Page Title & Actions */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Roles & Permissions</h1>
            <p className="text-slate-500 mt-1">Manage roles and their associated permissions for the platform.</p>
          </div>
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-all transform active:scale-95 shadow-lg shadow-primary/20"
          >
            <span className="material-symbols-outlined text-xl">add</span>
            Create Role
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-5 rounded-xl">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">Total Roles</p>
            <span className="text-2xl font-black text-slate-900">{roles.length}</span>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-xl">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">System Roles</p>
            <span className="text-2xl font-black text-primary">{systemCount}</span>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-xl">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">Customer Roles</p>
            <span className="text-2xl font-black text-blue-400">{customerCount}</span>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-500 text-sm font-medium flex items-center gap-3">
            <span className="material-symbols-outlined">error</span>
            {error}
            <button onClick={fetchData} className="ml-auto text-red-500 hover:text-red-600 underline text-xs">Retry</button>
          </div>
        )}

        {/* Table Container */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {(['all', 'system', 'customer'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-colors ${
                    activeFilter === filter
                      ? 'bg-slate-100 text-slate-700 border border-slate-200'
                      : 'text-slate-500 hover:text-slate-600'
                  }`}
                >
                  {filter === 'all' ? 'All Roles' : filter === 'system' ? 'System Roles' : 'Customer Roles'}
                </button>
              ))}
            </div>
          </div>

          {/* Loading / Empty / List */}
          {isLoading ? (
            <div className="p-16 flex items-center justify-center">
              <div className="flex items-center gap-3 text-slate-500">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">Loading roles...</span>
              </div>
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="p-16 flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-4xl text-slate-400 mb-3">shield</span>
              <p className="text-slate-500 font-bold">No roles found</p>
              <p className="text-slate-400 text-sm mt-1">
                {roles.length === 0 ? 'Create your first role to get started.' : 'Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div>
              {filteredRoles.map((role) => {
                const isExpanded = expandedRoleId === role.id;

                return (
                  <div key={role.id} className="border-b border-slate-200 last:border-b-0">
                    {/* Role Row */}
                    <div
                      className="px-6 py-4 flex items-center gap-4 hover:bg-primary/5 cursor-pointer group transition-colors"
                      onClick={() => setExpandedRoleId(isExpanded ? null : role.id)}
                    >
                      {/* Expand icon */}
                      <span className={`material-symbols-outlined text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                        chevron_right
                      </span>

                      {/* Role icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border border-slate-200 ${role.isSystemRole ? 'bg-primary/10 text-primary' : 'bg-blue-500/10 text-blue-400'}`}>
                        <span className="material-symbols-outlined text-xl">
                          {role.isSystemRole ? 'shield' : 'group'}
                        </span>
                      </div>

                      {/* Role info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <p className="text-sm font-bold text-slate-900">{role.name}</p>
                          <RoleTypeBadge isSystemRole={role.isSystemRole} />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {role.description || 'No description'}
                        </p>
                      </div>

                      {/* Permission count */}
                      <div className="text-right mr-4">
                        <p className="text-sm font-bold text-slate-600">{role.permissions.length}</p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">Permissions</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEdit(role)}
                          className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Edit Role"
                        >
                          <span className="material-symbols-outlined text-lg">edit</span>
                        </button>
                        <button
                          onClick={() => setDeleteTarget(role)}
                          className="p-2 text-slate-400 hover:text-primary transition-colors"
                          title="Delete Role"
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>
                    </div>

                    {/* Expanded Permissions */}
                    {isExpanded && (
                      <PermissionViewer
                        role={role}
                        allCategories={allCategories}
                        onSavePermissions={handleSavePermissions}
                        isSaving={isSavingPermissions}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          {!isLoading && filteredRoles.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 font-medium tracking-wide">
                Showing {filteredRoles.length} of {roles.length} roles
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Role Modal */}
      <CreateEditRoleModal
        isOpen={isModalOpen}
        editRole={editRole}
        allCategories={allCategories}
        onClose={() => { setIsModalOpen(false); setEditRole(null); }}
        onSuccess={fetchData}
      />

      {/* Delete Confirmation */}
      {deleteTarget && (
        <DeleteConfirm
          role={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
        />
      )}
    </main>
  );
};

export default RolesPermissionsPage;
